import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

// ログアウト状態を管理するためのセッションストレージキー
const LOGOUT_FLAG = 'logout_in_progress';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, error: authError, isAuthenticated } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [logoutComplete, setLogoutComplete] = useState(false);

  // ログイン画面表示時にログアウトフラグをチェック
  useEffect(() => {
    const wasLoggedOut = sessionStorage.getItem(LOGOUT_FLAG) === 'true';
    if (wasLoggedOut) {
      setLogoutComplete(true);
      // フラグをクリア
      sessionStorage.removeItem(LOGOUT_FLAG);
    }
  }, []);

  // 認証状態が変更されたときの処理
  useEffect(() => {
    if (isAuthenticated) {
      // ログイン後は、保存された戻り先(from)があればそこへ、無ければダッシュボードへ遷移する。
      const fromState = (location.state as any)?.from;
      const to = fromState
        ? `${fromState.pathname || '/dashboard'}${fromState.search || ''}`
        : '/dashboard';
      console.log('認証状態が更新されました。遷移先:', to);
      navigate(to, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLogoutComplete(false);

    if (!username || !password) {
      setError('ユーザー名とパスワードを入力してください。');
      return;
    }

    try {
      setIsLoading(true);
      console.log('ログインを試行します...');
      await login(username, password);
    } catch (err: any) {
      console.error('ログインエラー:', err);
      setError(err.message || 'ログインに失敗しました。認証情報を確認してください。');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography component="h1" variant="h5">
            ログイン
          </Typography>

          {logoutComplete && (
            <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
              ログアウトが完了しました。
            </Alert>
          )}

          {(error || authError) && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error || authError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="ユーザー名"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="パスワード"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'ログイン'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 